(() => {
  const observedCarousels = new WeakSet();

  function hideSingleProductMediaThumbnail() {
    document.querySelectorAll('.xo-product-info-media-carousel').forEach((carousel) => {
      const content = carousel.querySelector('.xo-product-info-media-carousel__content');
      const thumbCount = carousel.querySelectorAll('.xo-product-info-media-carousel__thumb-slide').length;
      if (content && thumbCount === 1) content.classList.add('xo-product-info-media-carousel__content--hidden');
    });
  }

  function updateVideoButton(button, isPlaying, labelPrefix) {
    if (!button) return;
    button.classList.toggle('is-playing', isPlaying);
    button.classList.toggle('is-paused', !isPlaying);
    button.setAttribute('aria-label', isPlaying ? `Pause ${labelPrefix}` : `Play ${labelPrefix}`);
  }

  function formatVideoTime(seconds) {
    if (!Number.isFinite(seconds)) return '0:00';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60).toString().padStart(2, '0');
    return `${minutes}:${remainingSeconds}`;
  }

  function getMainShell(mediaId) {
    return [...document.querySelectorAll('[data-product-media-video]')]
      .find((shell) => shell.dataset.productMediaVideo === mediaId) || null;
  }

  function getMainVideo(mediaId) {
    return getMainShell(mediaId)?.querySelector('video') || null;
  }

  function getExternalPlayer(shell) {
    return shell?.querySelector('iframe.js-youtube, iframe.js-vimeo') || null;
  }

  function getMediaIdForVideo(video) {
    return video?.closest('[data-product-media-video]')?.dataset.productMediaVideo || '';
  }

  function videoIsPlaying(video) {
    return Boolean(video && !video.paused && !video.ended);
  }

  function getVideoProgress(video) {
    if (!video || !Number.isFinite(video.duration) || video.duration <= 0) return 0;
    return Math.max(0, Math.min(100, (video.currentTime / video.duration) * 100));
  }

  function setProgress(root, progress) {
    root.querySelectorAll('[data-video-progress-fill]').forEach((fill) => {
      fill.style.width = `${progress}%`;
    });
  }

  function updateMediaGroup(mediaId, sourceVideo) {
    if (!mediaId) return;

    const video = sourceVideo || getMainVideo(mediaId);
    const shell = getMainShell(mediaId);
    const isPlaying = video ? videoIsPlaying(video) : shell?.dataset.externalVideoState === 'playing';
    const progress = getVideoProgress(video);
    const isMuted = Boolean(video?.muted);
    const timeText = `${formatVideoTime(video?.currentTime || 0)} / ${formatVideoTime(video?.duration || 0)}`;

    if (shell) {
      shell.classList.toggle('is-playing', isPlaying);
      shell.classList.toggle('is-paused', !isPlaying);
      shell.classList.toggle('is-muted', isMuted);
      shell.querySelectorAll('[data-video-toggle]').forEach((button) => updateVideoButton(button, isPlaying, 'video'));
      shell.querySelectorAll('[data-video-mute]').forEach((button) => {
        button.setAttribute('aria-label', isMuted ? 'Unmute video' : 'Mute video');
      });
      shell.querySelectorAll('[data-video-time]').forEach((time) => {
        time.textContent = timeText;
      });
      setProgress(shell, progress);
    }

    document.querySelectorAll('[data-product-media-thumb]').forEach((thumb) => {
      if (thumb.dataset.productMediaThumb !== mediaId) return;
      updateVideoButton(thumb.querySelector('[data-video-thumb-toggle], [data-external-video-thumb-toggle]'), isPlaying, 'video preview');
    });
  }

  function isSlideActive(slide) {
    if (!slide) return true;

    const list = slide.parentElement;
    const slides = list ? [...list.children].filter((child) => child.matches('xo-carousel-slide')) : [slide];
    const explicitlyActive = slides.filter((item) =>
      (item.hasAttribute('xo-active') && item.getAttribute('xo-active') !== 'false') ||
      item.classList.contains('xo-active')
    );

    if (explicitlyActive.length) return explicitlyActive.includes(slide);

    return slides[0] === slide;
  }

  function isShellActive(shell) {
    return isSlideActive(shell.closest('xo-carousel-slide'));
  }

  function pauseOtherMainVideos(activeVideo) {
    document.querySelectorAll('[data-product-media-video] video').forEach((video) => {
      if (video !== activeVideo && !video.paused) video.pause();
    });
  }

  function mountDeferredMedia(shell) {
    if (!shell) return null;

    const mountedMedia = shell.querySelector('[data-product-media-mounted]');
    if (mountedMedia) return mountedMedia;

    const template = shell.querySelector('template[data-product-media-video-template]');
    if (!template) return null;

    const frame = template.parentElement;
    const poster = frame.querySelector('[data-product-media-video-poster]');
    const fragment = template.content.cloneNode(true);
    const mountedNodes = [...fragment.children];
    mountedNodes.forEach((node) => node.setAttribute('data-product-media-mounted', ''));
    template.before(fragment);

    const media = mountedNodes.find((node) => node.matches('video, iframe')) || mountedNodes[0] || null;
    const hidePoster = () => {
      if (poster) poster.hidden = true;
    };

    if (media?.matches('video')) {
      if (media.readyState >= 2) hidePoster();
      else media.addEventListener('loadeddata', hidePoster, { once: true });
    } else if (media?.matches('iframe')) {
      media.addEventListener('load', hidePoster, { once: true });
    }

    return media;
  }

  function unmountDeferredMedia(shell) {
    if (!shell?.querySelector('template[data-product-media-video-template]')) return;

    const video = shell.querySelector('video[data-product-media-mounted]');
    if (video && !video.paused) video.pause();

    const player = shell.querySelector('iframe[data-product-media-mounted]');
    if (player && shell.dataset.externalVideoState === 'playing') {
      sendExternalVideoCommand(shell, false);
    }

    shell.querySelectorAll('[data-product-media-mounted]').forEach((node) => node.remove());
    const poster = shell.querySelector('[data-product-media-video-poster]');
    if (poster) poster.hidden = false;
    shell.dataset.externalVideoState = 'paused';
    updateMediaGroup(shell.dataset.productMediaVideo);
  }

  function waitForShellActive(shell, timeout = 5000) {
    if (!shell) return Promise.resolve(false);
    if (isShellActive(shell)) return Promise.resolve(true);

    const slide = shell.closest('xo-carousel-slide');
    if (!slide) return Promise.resolve(false);

    return new Promise((resolve) => {
      let settled = false;
      const finish = (active) => {
        if (settled) return;
        settled = true;
        observer.disconnect();
        window.clearTimeout(timer);
        resolve(active);
      };
      const observer = new MutationObserver(() => {
        if (isShellActive(shell)) finish(true);
      });
      const timer = window.setTimeout(() => finish(isShellActive(shell)), timeout);

      observer.observe(slide.parentElement || slide, {
        subtree: true,
        attributes: true,
        attributeFilter: ['xo-active', 'class'],
      });
    });
  }

  function seekVideoFromProgress(event, mediaId, progress) {
    const video = getMainVideo(mediaId);
    if (!video || !Number.isFinite(video.duration) || video.duration <= 0) return;

    const rect = progress.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
    video.currentTime = ratio * video.duration;
    updateMediaGroup(mediaId, video);
  }

  function toggleVideo(video) {
    if (!video) return;
    if (video.paused || video.ended) {
      pauseOtherMainVideos(video);
      const promise = video.play();
      if (promise && typeof promise.catch === 'function') promise.catch(() => {});
    } else {
      video.pause();
    }
  }

  function playVideoMuted(video) {
    if (!video) return;
    video.muted = true;
    pauseOtherMainVideos(video);
    const promise = video.play();
    if (promise && typeof promise.catch === 'function') promise.catch(() => {});
  }

  function setExternalVideoState(shell, isPlaying) {
    if (!shell) return;
    shell.dataset.externalVideoState = isPlaying ? 'playing' : 'paused';
    updateMediaGroup(shell.dataset.productMediaVideo);
  }

  function sendExternalVideoCommand(shell, shouldPlay) {
    const player = getExternalPlayer(shell);
    if (!player?.contentWindow) return;

    const sendCommand = () => {
      if (player.classList.contains('js-youtube')) {
        player.contentWindow?.postMessage(JSON.stringify({
          event: 'command',
          func: shouldPlay ? 'playVideo' : 'pauseVideo',
          args: [],
        }), '*');
      } else {
        player.contentWindow?.postMessage({ method: shouldPlay ? 'play' : 'pause' }, '*');
      }
    };

    sendCommand();
    if (player.dataset.externalVideoLoaded !== 'true') {
      player.addEventListener('load', sendCommand, { once: true });
    }
    setExternalVideoState(shell, shouldPlay);
    if (shouldPlay) {
      window.setTimeout(() => {
        if (!getExternalPlayer(shell) && shell.querySelector('[data-cky-tag="video-placeholder"]')) {
          setExternalVideoState(shell, false);
        }
      }, 250);
    }
  }

  function prepareExternalVideo(shell) {
    const player = getExternalPlayer(shell);
    if (!player) return;

    if (!shell.dataset.externalVideoState) shell.dataset.externalVideoState = 'paused';
    if (player.dataset.externalVideoReady === 'true') return;
    player.dataset.externalVideoReady = 'true';

    const registerPlayerEvents = () => {
      player.dataset.externalVideoLoaded = 'true';
      if (player.classList.contains('js-youtube')) {
        player.contentWindow?.postMessage(JSON.stringify({
          event: 'listening',
          id: shell.dataset.productMediaVideo,
        }), '*');
      } else {
        ['play', 'pause', 'ended'].forEach((eventName) => {
          player.contentWindow?.postMessage({ method: 'addEventListener', value: eventName }, '*');
        });
      }
    };

    player.addEventListener('load', registerPlayerEvents, { once: true });
    registerPlayerEvents();
    updateMediaGroup(shell.dataset.productMediaVideo);
  }

  function toggleExternalVideo(shell) {
    sendExternalVideoCommand(shell, shell?.dataset.externalVideoState !== 'playing');
  }

  function prepareVideo(video, mediaId) {
    if (!video) return;

    video.controls = false;
    video.removeAttribute('controls');
    video.playsInline = true;
    video.style.pointerEvents = 'none';

    if (video.dataset.videoMediaReady === 'true') return;
    video.dataset.videoMediaReady = 'true';

    ['play', 'pause', 'ended', 'timeupdate', 'loadedmetadata', 'volumechange'].forEach((eventName) => {
      video.addEventListener(eventName, () => updateMediaGroup(mediaId, video));
    });
  }

  function prepareShell(shell) {
    const mediaId = shell.dataset.productMediaVideo;
    const video = shell.querySelector('video');
    if (video) prepareVideo(video, mediaId);
    else prepareExternalVideo(shell);

    if (shell.dataset.videoDemandControllerReady !== 'true') {
      shell.dataset.videoDemandControllerReady = 'true';
      shell.addEventListener('click', (event) => {
        const control = event.target.closest('[data-video-toggle], [data-video-mute], [data-video-progress]');
        if (!control || !shell.contains(control)) return;

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        if (!isShellActive(shell)) return;

        const mountedMedia = mountDeferredMedia(shell);
        const currentVideo = mountedMedia?.matches('video') ? mountedMedia : shell.querySelector('video');
        if (currentVideo) prepareVideo(currentVideo, mediaId);
        else prepareExternalVideo(shell);

        if (control.matches('[data-video-toggle]')) {
          if (currentVideo) toggleVideo(currentVideo);
          else toggleExternalVideo(shell);
        } else if (control.matches('[data-video-mute]')) {
          if (!currentVideo) return;
          currentVideo.muted = !currentVideo.muted;
          updateMediaGroup(mediaId, currentVideo);
        } else {
          seekVideoFromProgress(event, mediaId, control);
        }
      }, true);
    }

    updateMediaGroup(mediaId, video);
  }

  function syncActiveMedia(carousel) {
    carousel.querySelectorAll('[data-product-media-video]').forEach((shell) => {
      if (isShellActive(shell)) {
        prepareShell(shell);
      } else {
        unmountDeferredMedia(shell);
      }
    });
  }

  function observeCarousel(carousel) {
    if (observedCarousels.has(carousel)) return;
    observedCarousels.add(carousel);

    let syncFrame = null;
    const observer = new MutationObserver((mutations) => {
      const activeSlideChanged = mutations.some((mutation) =>
        mutation.target instanceof Element &&
        mutation.target.matches('xo-carousel-slide')
      );

      if (!activeSlideChanged || syncFrame !== null) return;

      syncFrame = window.requestAnimationFrame(() => {
        syncFrame = null;
        syncActiveMedia(carousel);
      });
    });
    observer.observe(carousel, {
      subtree: true,
      attributes: true,
      attributeFilter: ['xo-active', 'xo-visible', 'class'],
    });
  }

  async function scheduleThumbPlayback(thumb) {
    if (thumb.dataset.videoDemandPending === 'true') return;
    thumb.dataset.videoDemandPending = 'true';
    const mediaId = thumb.dataset.productMediaThumb;

    try {
      const shell = getMainShell(mediaId);
      if (!shell || !await waitForShellActive(shell)) return;

      const carousel = shell?.closest('.xo-product-info-media-carousel, .xo-product-info-media-slide');
      if (carousel) syncActiveMedia(carousel);
      if (!isShellActive(shell)) return;

      const mountedMedia = mountDeferredMedia(shell);
      prepareShell(shell);
      const video = mountedMedia?.matches('video') ? mountedMedia : shell.querySelector('video');
      if (video) {
        prepareVideo(video, mediaId);
        playVideoMuted(video);
      } else {
        prepareExternalVideo(shell);
        sendExternalVideoCommand(shell, true);
      }
    } finally {
      delete thumb.dataset.videoDemandPending;
    }
  }

  function prepareThumbnail(thumb) {
    const button = thumb.querySelector('[data-video-thumb-toggle], [data-external-video-thumb-toggle]');

    if (thumb.dataset.videoDemandThumbReady !== 'true') {
      thumb.dataset.videoDemandThumbReady = 'true';
      thumb.addEventListener('click', (event) => {
        if (button && event.target.closest('[data-video-thumb-toggle], [data-external-video-thumb-toggle]')) {
          const shell = getMainShell(thumb.dataset.productMediaThumb);
          if (!shell || !isShellActive(shell)) {
            scheduleThumbPlayback(thumb);
            return;
          }

          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();

          const mountedMedia = mountDeferredMedia(shell);
          prepareShell(shell);
          const video = mountedMedia?.matches('video') ? mountedMedia : getMainVideo(thumb.dataset.productMediaThumb);
          if (video) {
            prepareVideo(video, thumb.dataset.productMediaThumb);
            toggleVideo(video);
          } else {
            prepareExternalVideo(shell);
            toggleExternalVideo(shell);
          }
          return;
        }

        scheduleThumbPlayback(thumb);
      }, true);
    }

    updateMediaGroup(thumb.dataset.productMediaThumb, getMainVideo(thumb.dataset.productMediaThumb));
  }

  function initProductMediaVideoControls(root = document) {
    hideSingleProductMediaThumbnail();

    const carousels = root.matches?.('.xo-product-info-media-carousel, .xo-product-info-media-slide')
      ? [root]
      : root.querySelectorAll('.xo-product-info-media-carousel, .xo-product-info-media-slide');

    carousels.forEach((carousel) => {
      observeCarousel(carousel);
      syncActiveMedia(carousel);
      carousel.querySelectorAll('[data-product-media-thumb]').forEach(prepareThumbnail);
    });
  }

  window.addEventListener('message', (event) => {
    let messageHost = '';
    try {
      messageHost = new URL(event.origin).hostname;
    } catch {
      return;
    }
    if (!/youtube(?:-nocookie)?\.com$/.test(messageHost) && !/vimeo\.com$/.test(messageHost)) return;

    const shell = [...document.querySelectorAll('[data-product-media-video]')]
      .find((item) => getExternalPlayer(item)?.contentWindow === event.source);
    if (!shell) return;

    let payload = event.data;
    if (typeof payload === 'string') {
      try {
        payload = JSON.parse(payload);
      } catch {
        return;
      }
    }

    const youtubeState = payload?.info?.playerState ?? (payload?.event === 'onStateChange' ? payload.info : null);
    if (youtubeState === 1 || payload?.event === 'play') {
      setExternalVideoState(shell, true);
    } else if ([0, 2].includes(youtubeState) || ['pause', 'ended'].includes(payload?.event)) {
      setExternalVideoState(shell, false);
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initProductMediaVideoControls());
  } else {
    initProductMediaVideoControls();
  }

  window.addEventListener('load', () => initProductMediaVideoControls());
  document.addEventListener('change', () => initProductMediaVideoControls());
  document.addEventListener('shopify:section:load', (event) => initProductMediaVideoControls(event.target));
})();
